import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { stringify } from 'yaml';
import {
  Badge,
  Button,
  cn,
  Flex,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from '@kognitos/lattice';
import { formatDisplayName, formatNumber } from '@/shared/utils';
import {
  detectFileType,
  getFileExtension,
} from '@/modules/file-renderer/utils';
import { EFileType } from '@/modules/file-renderer/types';
import { OutputRenderer } from '@/modules/outputs/components/OutputRenderer';
import {
  EOutputCardSize,
  EOutputCardVariant,
  ERenderMode,
  type TResolvedValue,
} from '@/modules/outputs/types';
import {
  getResolvedFileDisplayName,
  isRemoteValue,
  isTable,
} from '@/modules/outputs/utils';
import { OutputGlyph } from './OutputGlyph';
import {
  getOutputCardSummary,
  getOutputFileNames,
  getOutputSourceText,
  getOutputTablePreview,
  isOutputFileList,
  OUTPUT_CARD_MAX_FRACTION_DIGITS,
} from './output-card-utils';
import { OutputMediaPreview } from './output-preview-primitives';
import { IDPValueThumbnail } from './renderers/IDPDocumentThumbnail';
import { OutputTableCell } from './OutputTableCell';
import { getNumericOutputColumns } from './output-table-order';

interface IOutputCardPreviewProps {
  value: TResolvedValue;
  variant: EOutputCardVariant;
  size: EOutputCardSize.MD | EOutputCardSize.LG;
  outputName: string;
  scrollParent?: HTMLElement | null;
  onExpand?: (name: string, value: TResolvedValue) => void;
}

function TextPreview({ text, large }: { text: string; large: boolean }) {
  return (
    <Text
      as="span"
      color="default"
      weight="normal"
      className={cn(
        'block whitespace-pre-wrap break-words [font-variant-numeric:tabular-nums]',
        large ? 'text-sm leading-5' : 'p-2 text-[11px] leading-4',
      )}
    >
      {text}
    </Text>
  );
}

function NumberPreview({ value, large }: { value: number; large: boolean }) {
  return (
    <Flex
      align="center"
      justify="start"
      className={cn(
        'min-h-full font-medium [font-variant-numeric:tabular-nums]',
        !large && 'px-5 py-3',
      )}
    >
      <Text as="span" level="small" weight="medium" className="leading-5">
        {formatNumber(value, OUTPUT_CARD_MAX_FRACTION_DIGITS)}
      </Text>
    </Flex>
  );
}

function FileListPreview({
  value,
  large,
}: {
  value: TResolvedValue;
  large: boolean;
}) {
  const { t } = useTranslation('outputs');
  const names = getOutputFileNames(value);
  return (
    <Flex
      direction="column"
      gap={0.5}
      className={cn(
        'min-h-full w-full',
        !large && 'justify-center px-1.5 py-1',
      )}
    >
      {names.map((name, index) => (
        <Flex
          // A step can return two files with the same name, so position is the
          // only stable identity here.
          // eslint-disable-next-line react-x/no-array-index-key -- reason: file names are not unique within a list
          key={`${name}-${index}`}
          align="center"
          gap={2}
          className={cn(
            'w-full min-w-0 rounded-lg hover:bg-muted',
            large ? 'px-2 py-1.5' : 'px-0.5 py-1',
          )}
        >
          <OutputGlyph
            variant={EOutputCardVariant.FILE}
            size={large ? EOutputCardSize.LG : EOutputCardSize.MD}
          />
          <Flex direction="column" className="min-w-0 flex-1">
            <Text
              level="xSmall"
              color="default"
              weight={large ? 'medium' : 'normal'}
              className={cn('truncate', !large && 'text-[11px] leading-[15px]')}
            >
              {name}
            </Text>
            <Text
              level="xSmall"
              color="muted"
              weight="normal"
              className={cn(!large && 'text-[10px] leading-[14px]')}
            >
              {getFileExtension(name).toUpperCase() ||
                t('outputs.card.fileMeta')}
            </Text>
          </Flex>
        </Flex>
      ))}
    </Flex>
  );
}

function TablePreview({
  value,
  large,
}: {
  value: TResolvedValue;
  large: boolean;
}) {
  const { t } = useTranslation('outputs');
  const preview = useMemo(() => getOutputTablePreview(value), [value]);
  const headers = preview.headers.slice(0, large ? undefined : 3);
  const rows = preview.rows.slice(0, large ? undefined : 8);
  // The values decide which columns are quantities, and the header follows its
  // cells so a right-aligned column never sits under a left-aligned label.
  const numericColumns = useMemo(
    () => getNumericOutputColumns(preview.headers, preview.rows),
    [preview],
  );

  if (headers.length === 0) {
    return (
      <Flex align="center" justify="center" className="min-h-full p-4">
        <Text level="xSmall" color="muted">
          {t('outputs.card.tablePreview')}
        </Text>
      </Flex>
    );
  }

  return (
    <Table
      className={cn(
        large ? 'text-sm' : 'table-fixed text-[10px] leading-[14px]',
      )}
    >
      <TableHeader>
        <TableRow>
          {headers.map((header, headerIndex) => (
            <TableHead
              key={header}
              className={cn(
                'max-w-40 truncate text-muted-foreground',
                large
                  ? 'h-auto px-5 py-2.5 text-xs leading-4'
                  : 'h-auto px-2 py-1 text-[9px] leading-[13px]',
                numericColumns[headerIndex] && 'text-right',
              )}
            >
              {formatDisplayName(header)}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, rowIndex) => (
          // eslint-disable-next-line react-x/no-array-index-key -- reason: parsed table rows have no stable id and retain source order
          <TableRow key={rowIndex}>
            {headers.map((header, columnIndex) => (
              <TableCell
                key={header}
                className={cn(
                  'max-w-48 truncate [font-variant-numeric:tabular-nums]',
                  large ? 'px-5 py-2.5' : 'px-2 py-1',
                  numericColumns[columnIndex] && 'text-right',
                )}
              >
                {large ? (
                  <OutputTableCell
                    header={header}
                    value={row[columnIndex] ?? ''}
                  />
                ) : (
                  row[columnIndex] || '—'
                )}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function BrowserPreview() {
  return (
    <Flex
      direction="column"
      className="min-h-full overflow-hidden rounded-lg border border-border bg-background"
    >
      <Flex
        align="center"
        gap={0.5}
        className="h-4 shrink-0 border-b border-border bg-foreground/5 px-1.5"
      >
        <span className="size-1 rounded-full bg-foreground/25" />
        <span className="size-1 rounded-full bg-foreground/25" />
        <span className="size-1 rounded-full bg-foreground/25" />
        <span className="ml-1 h-2 flex-1 rounded bg-foreground/10" />
      </Flex>
      <Flex direction="column" gap={1} className="relative flex-1 p-2">
        <span className="h-1 w-2/5 bg-foreground/20" />
        <span className="h-1 w-full bg-foreground/10" />
        <span className="h-1 w-5/6 bg-foreground/10" />
      </Flex>
    </Flex>
  );
}

type TYamlToken = { text: string; kind: 'key' | 'string' | 'number' | 'muted' };

function tokenizeYamlLine(line: string): TYamlToken[] {
  if (line.trimStart().startsWith('#')) return [{ text: line, kind: 'muted' }];
  const match = line.match(/^(\s*(?:-\s+)?[^:]+:)(.*)$/);
  if (!match) return [{ text: line, kind: 'muted' }];
  const value = match[2];
  const kind = /^\s*-?\d+(?:\.\d+)?\s*$/.test(value)
    ? 'number'
    : value.trim().length === 0
      ? 'muted'
      : 'string';
  return [
    { text: match[1], kind: 'key' },
    { text: value, kind },
  ];
}

function YamlLines({
  value,
  large,
  className,
}: {
  value: TResolvedValue;
  large: boolean;
  className?: string;
}) {
  const lines = stringify(value, { indent: 2 }).trimEnd().split('\n');
  return (
    <Flex direction="column" className={cn(!large && 'p-2', className)}>
      {lines.map((line, index) => (
        <span
          // eslint-disable-next-line react-x/no-array-index-key -- reason: serialized source lines retain their stable order
          key={index}
          className={cn(
            'whitespace-pre-wrap break-words font-mono',
            large ? 'text-[13px] leading-[21px]' : 'text-[10px] leading-[15px]',
          )}
        >
          {tokenizeYamlLine(line).map((token, tokenIndex) => (
            <span
              // eslint-disable-next-line react-x/no-array-index-key -- reason: a line contains at most two ordered syntax tokens
              key={tokenIndex}
              className={`output-card__yaml-${token.kind}`}
            >
              {token.text}
            </span>
          ))}
        </span>
      ))}
    </Flex>
  );
}

function isNestedTable(value: TResolvedValue): boolean {
  return isTable(value) || (Array.isArray(value) && !isOutputFileList(value));
}

function YamlPreview({
  value,
  large,
  outputName,
  onExpand,
}: {
  value: TResolvedValue;
  large: boolean;
  outputName: string;
  onExpand?: (name: string, value: TResolvedValue) => void;
}) {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return <YamlLines value={value} large={large} />;
  }

  const entries = Object.entries(value);
  if (!entries.some(([, nestedValue]) => isNestedTable(nestedValue))) {
    return <YamlLines value={value} large={large} />;
  }

  return (
    <Flex direction="column" gap={1} className={cn(!large && 'p-2')}>
      {entries.map(([key, nestedValue]) => {
        if (!isNestedTable(nestedValue)) {
          return (
            <YamlLines
              key={key}
              value={{ [key]: nestedValue }}
              large={large}
              className="p-0"
            />
          );
        }

        const nestedName = `${outputName}.${key}`;
        const summary = getOutputCardSummary(
          nestedValue,
          EOutputCardVariant.TABLE,
        );
        return (
          <Flex key={key} direction="column" gap={1}>
            <span
              className={cn(
                'output-card__yaml-key font-mono',
                large
                  ? 'text-[13px] leading-[21px]'
                  : 'text-[10px] leading-[15px]',
              )}
            >
              {key}:
            </span>
            <Button
              variant="outline"
              size="sm"
              className="ml-5 h-auto w-[280px] max-w-[calc(100%-1.25rem)] justify-start gap-3 rounded-2xl p-3 text-left"
              onClick={event => {
                event.stopPropagation();
                onExpand?.(nestedName, nestedValue);
              }}
              aria-label={`${key}: ${summary}`}
            >
              <OutputGlyph
                variant={EOutputCardVariant.TABLE}
                size={EOutputCardSize.SM}
              />
              <Flex direction="column" className="min-w-0 flex-1">
                <Text level="xSmall" color="muted" className="truncate">
                  {formatDisplayName(key)}
                </Text>
                <Text level="small" className="truncate">
                  {summary}
                </Text>
              </Flex>
              <Icon type="Maximize" size="xs" className="shrink-0" />
            </Button>
          </Flex>
        );
      })}
    </Flex>
  );
}

/** Shared content implementation used by md cards and the sm hover card. */
export function OutputCardPreview({
  value,
  variant,
  size,
  outputName,
  scrollParent,
  onExpand,
}: IOutputCardPreviewProps) {
  const { t } = useTranslation('outputs');
  const large = size === EOutputCardSize.LG;

  if (isRemoteValue(value)) {
    return (
      <Flex
        align="center"
        justify="center"
        gap={2}
        className={cn('min-h-full', !large && 'p-4')}
      >
        <OutputGlyph variant={variant} size={size} loading />
        <Text level="xSmall" color="muted">
          {t('outputs.card.loading')}
        </Text>
      </Flex>
    );
  }

  switch (variant) {
    case EOutputCardVariant.TEXT:
      return <TextPreview text={String(value)} large={large} />;
    case EOutputCardVariant.NUMBER:
      return typeof value === 'number' ? (
        <NumberPreview value={value} large={large} />
      ) : (
        <TextPreview text={String(value ?? '')} large={large} />
      );
    case EOutputCardVariant.LIST:
      return <FileListPreview value={value} large={large} />;
    case EOutputCardVariant.TABLE:
      return <TablePreview value={value} large={large} />;
    case EOutputCardVariant.IDP:
      return large ? (
        <OutputRenderer
          value={value}
          mode={ERenderMode.PREVIEW}
          outputName={outputName}
          scrollParent={scrollParent}
        />
      ) : (
        <IDPValueThumbnail value={value} />
      );
    case EOutputCardVariant.FILE:
      return large ? (
        <OutputRenderer
          value={value}
          mode={ERenderMode.PREVIEW}
          outputName={outputName}
          scrollParent={scrollParent}
        />
      ) : (
        <OutputMediaPreview value={value} />
      );
    case EOutputCardVariant.CODE:
      return large ? (
        <OutputRenderer
          value={value}
          mode={ERenderMode.PREVIEW}
          outputName={outputName}
        />
      ) : (
        <TextPreview text={getOutputSourceText(value)} large={false} />
      );
    case EOutputCardVariant.BROWSER:
      // A card with room for it plays the session — live screencast while the
      // browser is working, recording once it finishes. Only the small card,
      // which has no room to watch anything, settles for a still. The card
      // header already has the expand button, so the player stays minimal.
      return large ? (
        <OutputRenderer
          value={value}
          mode={ERenderMode.PREVIEW}
          outputName={outputName}
          minimal
        />
      ) : (
        <BrowserPreview />
      );
    case EOutputCardVariant.DICT:
      return (
        <YamlPreview
          value={value}
          large={large}
          outputName={outputName}
          onExpand={onExpand}
        />
      );
    case EOutputCardVariant.EMPTY:
      return (
        <Flex
          align="center"
          justify="center"
          gap={2}
          className={cn('min-h-full', !large && 'py-0.5')}
        >
          <Text level="xSmall" color="muted" weight="normal">
            {t('outputs.card.noValue')}
          </Text>
        </Flex>
      );
  }
}

/** Hover metadata for document previews other than HTML. */
export function OutputDocumentMetadata({
  value,
  variant,
}: {
  value: TResolvedValue;
  variant: EOutputCardVariant;
}) {
  const { t } = useTranslation('outputs');
  if (
    variant === EOutputCardVariant.FILE &&
    detectFileType(getResolvedFileDisplayName(value) ?? '') === EFileType.HTML
  )
    return null;

  return (
    <Badge
      variant="secondary"
      className="pointer-events-none absolute bottom-3 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap opacity-0 shadow-sm transition-opacity group-hover/output-card:opacity-100"
    >
      {getOutputCardSummary(value, variant)} · {t('outputs.card.document')}
    </Badge>
  );
}
